'use client'

import { ClipboardPaste, FileUp, type LucideIcon } from 'lucide-react'

/** The import sources the wizard can branch into. */
export type ImportSource = 'upload' | 'paste'

interface SourcePickerStepProps {
	/** Fired with the chosen source to advance the wizard to that branch. */
	onPick: (source: ImportSource) => void
}

/**
 * A single tappable source option. Full-width card with a leading icon, a title,
 * and muted subtext. Rendered as a real
 * `<button>` so it's keyboard-focusable and announces as a control. The title /
 * subtext are static copy (developer-authored), not user data, so they render
 * as plain children.
 */
function SourceOption({
	icon: Icon,
	title,
	subtext,
	onClick,
}: {
	icon: LucideIcon
	title: string
	subtext: string
	onClick: () => void
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			className="flex w-full items-start gap-3 rounded-lg border border-border px-4 py-3 text-left transition-colors hover:bg-surface-hover"
		>
			<Icon className="mt-0.5 size-5 shrink-0 text-primary" aria-hidden />
			<span className="min-w-0 flex-1">
				<span className="text-sm font-medium text-text-primary">{title}</span>
				<span className="mt-0.5 block text-xs text-text-muted">{subtext}</span>
			</span>
		</button>
	)
}

/**
 * Step 0 of the import wizard: choose where the route comes from.
 *
 * Presents full-width option cards — Upload a file and Paste a route — and
 * reports the pick upward so the orchestrator can
 * swap in the matching step. Holds no state of its own; it's a pure chooser.
 */
export function SourcePickerStep({ onPick }: SourcePickerStepProps) {
	return (
		<div className="flex flex-col gap-2">
			<SourceOption
				icon={FileUp}
				title="Upload a file"
				subtext="CSV (Excel: Save As → CSV)"
				onClick={() => onPick('upload')}
			/>
			<SourceOption
				icon={ClipboardPaste}
				title="Paste a route"
				subtext="Drop in text from an email, itinerary, or PDF"
				onClick={() => onPick('paste')}
			/>
		</div>
	)
}
